import { ROOMS_CREDENTIALS_STORE_KEY } from 'app/models/localStorage/constants';
import { useMemoOne } from 'app/utils/useMemoOne';
import { createStorageHook } from 'app/components/storage/createStorageHook';
import { StorageType } from 'app/utils/StorageContext';
import * as io from 'io-ts';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';
import { decodeOrElse } from 'app/utils/ioTs';

interface IRoomsCredentialsContext {
  getRoomSecret(roomId: string): string | undefined;
  setRoomSecret(roomId: string, secret: string): void;
  deleteRoomSecret(roomId: string): void;
}

interface IRoomCredentialsEntry {
  secret?: string;
}

interface IRoomCredentialsStore {
  [id: string]: IRoomCredentialsEntry | undefined;
}

const RoomCredentialsCodec: io.Type<IRoomCredentialsStore> = io.dictionary(
  IdentityEntityStringIdCodec,
  io.union([
    io.undefined,
    io.partial({
      secret: io.string,
    }),
  ]),
);

const useRoomsCredentialsStore = createStorageHook<IRoomCredentialsStore | null>(
  ROOMS_CREDENTIALS_STORE_KEY,
  {
    serialize: (value) => value ? RoomCredentialsCodec.encode(value) : null,
    deserialize: (value) => decodeOrElse(RoomCredentialsCodec, value, () => null),
  },
  StorageType.LOCAL,
);

export const useRoomsCredentials = (): IRoomsCredentialsContext => {
  const [roomsCredentials, setRoomsCredentials] = useRoomsCredentialsStore();

  const actions = useMemoOne((): IRoomsCredentialsContext => {
    const update = (roomId: string, part: Partial<IRoomCredentialsEntry>) => {
      const updatedStore = {
        ...roomsCredentials,
        [roomId]: {
          ...roomsCredentials?.[roomId],
          ...part,
        },
      };

      setRoomsCredentials(updatedStore);
    };

    return {
      getRoomSecret: roomId => roomsCredentials?.[roomId]?.secret,
      setRoomSecret: (roomId, secret) => update(roomId, { secret }),
      deleteRoomSecret: roomId => {
        if (!roomsCredentials) return;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [roomId]: _, ...updatedStore } = roomsCredentials;

        setRoomsCredentials(updatedStore);
      },
    };
  }, [roomsCredentials, setRoomsCredentials]);

  return actions;
};
